// Aurora Innovation, Inc. Proprietary and Confidential. Copyright 2026.
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { checkboxClasses } from '@mui/material/Checkbox';
import { formControlLabelClasses } from '@mui/material/FormControlLabel';
import { radioClasses } from '@mui/material/Radio';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { switchClasses } from '@mui/material/Switch';
import React from 'react';
import { rem } from '../../tokens/conversions';
import { borderRadius } from '../../tokens/spacing';
import { CheckboxEmptyIcon, CheckboxIcon, CheckboxIndeterminateIcon } from '../icons';
import { checkboxColors } from './checkboxColors';
export var Checkbox = function(mode) {
    var c = checkboxColors(mode);
    var _obj, _obj1, _obj2, _obj3;
    var override = {
        MuiCheckbox: {
            styleOverrides: {
                root: (_obj1 = {
                    color: c.default.border,
                    padding: rem(0),
                    borderRadius: rem(3),
                    marginRight: rem(0),
                    border: "".concat(rem(1), " solid transparent"),
                    boxSizing: 'border-box',
                    '&:hover': {
                        color: c.hover.border,
                        backgroundColor: c.hover.background,
                        borderRadius: rem(borderRadius.small)
                    },
                    '&:focus-within': {
                        borderColor: c.focus.border
                    }
                }, _define_property(_obj1, "&.".concat(checkboxClasses.checked), {
                    color: c.checked.icon,
                    '&:hover': {
                        color: c.checked.icon
                    }
                }), _define_property(_obj1, "&.".concat(checkboxClasses.indeterminate), {
                    color: c.checked.icon,
                    '&:hover': {
                        color: c.checked.icon
                    }
                }), _define_property(_obj1, "&.".concat(checkboxClasses.disabled), (_obj = {
                    color: c.disabled.background
                }, // Disabled unchecked: Figma shows a solid filled box (no border)
                _define_property(_obj, "&:not(.".concat(checkboxClasses.checked, "):not(.").concat(checkboxClasses.indeterminate, ")"), {
                    '& .checkboxEmptyFill': {
                        fill: 'currentColor'
                    },
                    '& .checkboxEmptyBorder': {
                        display: 'none'
                    }
                }), _define_property(_obj, "&.".concat(checkboxClasses.checked, ", &.").concat(checkboxClasses.indeterminate), {
                    color: c.disabled.checked
                }), _obj)), _define_property(_obj1, "&.".concat(checkboxClasses.sizeSmall), _define_property({
                    width: rem(16),
                    height: rem(16),
                    borderRadius: rem(2)
                }, "& .".concat(svgIconClasses.root), {
                    fontSize: rem(16)
                })), _define_property(_obj1, "&.".concat(checkboxClasses.sizeMedium), _define_property({
                    width: rem(24),
                    height: rem(24),
                    borderRadius: rem(3)
                }, "& .".concat(svgIconClasses.root), {
                    fontSize: rem(24)
                })), _obj1)
            },
            variants: [
                {
                    props: {
                        size: 'large'
                    },
                    style: (_obj2 = {
                        width: rem(32),
                        height: rem(32),
                        borderRadius: rem(4)
                    }, _define_property(_obj2, "& .".concat(svgIconClasses.root), {
                        fontSize: rem(32)
                    }), _define_property(_obj2, '&:focus-within', {
                        borderWidth: rem(2)
                    }), _obj2)
                }
            ],
            defaultProps: {
                disableRipple: true,
                color: 'primary',
                checkedIcon: /*#__PURE__*/ React.createElement(CheckboxIcon, null),
                icon: /*#__PURE__*/ React.createElement(CheckboxEmptyIcon, null),
                indeterminateIcon: /*#__PURE__*/ React.createElement(CheckboxIndeterminateIcon, null)
            }
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: (_obj3 = {
                    color: c.default.text,
                    gap: rem(4)
                }, _define_property(_obj3, "& .".concat(checkboxClasses.sizeSmall), _define_property({}, "& + .".concat(formControlLabelClasses.label), {
                    fontSize: rem(12),
                    lineHeight: rem(16)
                })), _define_property(_obj3, "& .".concat(checkboxClasses.sizeMedium), _define_property({}, "& + .".concat(formControlLabelClasses.label), {
                    fontSize: rem(14),
                    lineHeight: rem(18)
                })), _define_property(_obj3, "& .MuiCheckbox-sizeLarge", _define_property({}, "& + .".concat(formControlLabelClasses.label), {
                    fontSize: rem(16),
                    lineHeight: rem(22)
                })), // MUI's default -11px accounts for the checkbox's own internal padding,
                // which this design system zeroes out (see MuiCheckbox padding above).
                _define_property(_obj3, "&:has(.".concat(checkboxClasses.root, ")"), {
                    marginLeft: rem(0)
                }), // Radio hover: Figma shows background on the entire row
                _define_property(_obj3, "&:has(.MuiRadio-root):hover", {
                    backgroundColor: c.hover.background,
                    borderRadius: rem(borderRadius.small),
                    overflow: 'clip'
                }), _define_property(_obj3, "&:has(.".concat(radioClasses.disabled, "):hover"), {
                    backgroundColor: 'transparent'
                }), _define_property(_obj3, "&:has(.MuiRadio-root)", {
                    marginLeft: rem(0)
                }), _define_property(_obj3, "&:has(.MuiRadio-root) .".concat(formControlLabelClasses.label), {
                    paddingRight: rem(4)
                }), _define_property(_obj3, "& .".concat(radioClasses.sizeSmall), _define_property({}, "& + .".concat(formControlLabelClasses.label), {
                    fontSize: rem(12),
                    lineHeight: rem(14)
                })), _define_property(_obj3, "& .MuiRadio-sizeMedium", _define_property({}, "& + .".concat(formControlLabelClasses.label), {
                    fontSize: rem(14),
                    lineHeight: rem(18)
                })), _define_property(_obj3, "& .MuiRadio-sizeLarge", _define_property({}, "& + .".concat(formControlLabelClasses.label), {
                    fontSize: rem(16),
                    lineHeight: rem(22)
                })), // Switch hover: Figma shows background on the entire row
                _define_property(_obj3, "&:has(.".concat(switchClasses.root, ")"), _define_property({
                    gap: rem(4),
                    marginLeft: rem(0),
                    borderRadius: rem(borderRadius.small)
                }, "& .".concat(formControlLabelClasses.label), {
                    paddingRight: rem(4)
                })), // Figma's Medium+Hover variant uses label pr 8px while every other
                // state uses 4px. Applying it on hover changes the row width and makes
                // right-aligned switches jump, so padding stays constant across states.
                _define_property(_obj3, "&:has(.".concat(switchClasses.root, "):hover"), {
                    backgroundColor: c.hover.background
                }), _define_property(_obj3, "&:has(.".concat(switchClasses.disabled, "):hover"), {
                    backgroundColor: 'transparent'
                }), _define_property(_obj3, "&.".concat(formControlLabelClasses.disabled), _define_property({
                    color: c.disabled.text
                }, "& .".concat(formControlLabelClasses.label), {
                    color: c.disabled.text
                })), _define_property(_obj3, "& .".concat(switchClasses.sizeSmall), _define_property({}, "& ~ .".concat(formControlLabelClasses.label), {
                    fontSize: rem(14),
                    lineHeight: rem(18)
                })), _define_property(_obj3, "& .".concat(switchClasses.sizeMedium), _define_property({}, "& ~ .".concat(formControlLabelClasses.label), {
                    fontSize: rem(14),
                    lineHeight: rem(18)
                })), _define_property(_obj3, "& .MuiSwitch-sizeLarge", _define_property({}, "& ~ .".concat(formControlLabelClasses.label), {
                    fontSize: rem(16),
                    lineHeight: rem(22)
                })), // Large switch row is taller to accommodate the larger toggle
                _define_property(_obj3, "&:has(.MuiSwitch-sizeLarge)", {
                    height: rem(32),
                    paddingLeft: rem(2),
                    alignItems: 'center'
                }), _obj3),
                asterisk: _define_property({
                    color: c.error.asterisk
                }, "&.".concat(formControlLabelClasses.error), {
                    color: c.error.asterisk
                })
            }
        }
    };
    return override;
};

//# sourceMappingURL=Checkbox.js.map